// ABOUTME: Fetches the realtime connection token through the GraphQL session the app already has,
// ABOUTME: and reads the identity the token carries; a session denial becomes centrifuge's UnauthorizedError.
import type { ApolloClient } from "@apollo/client"
import type { GraphQLFormattedError } from "graphql"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { UnauthorizedError } from "centrifuge"
import { RealtimeConnectionTokenDocument } from "@/graphql/main/gql/graphql"

/** The one GraphQL error the app treats as "no session" (see useMe): reconnecting would only repeat it. */
const SESSION_DENIED = "ErrAccessDenied"

/**
 * The server does not know this query — the front is deployed ahead of the
 * gql-api that answers `realtimeConnectionToken`, or behind a rollback. Asking
 * again cannot change the schema, so it is a refusal like a session denial: the
 * app degrades to its five-minute polling either way, and without this every
 * signed-in tab retried on centrifuge's backoff, forever, until the API caught
 * up.
 */
const SCHEMA_MISMATCH = new Set(["GRAPHQL_VALIDATION_FAILED", "GRAPHQL_PARSE_FAILED"])

const isPermanent = (error: GraphQLFormattedError): boolean =>
  error.message === SESSION_DENIED ||
  (typeof error.extensions?.code === "string" && SCHEMA_MISMATCH.has(error.extensions.code)) ||
  // gqlgen answers a query for a field it does not have with a validation
  // error, but not every gateway in front of it keeps the extension code — and
  // gqlerror prefixes the source position, so the message arrives as
  // `input:2:3: Cannot query field ...` rather than starting with it.
  error.message.includes("Cannot query field")

/**
 * The server answers `realtimeConnectionToken: null` when it has no Centrifugo
 * secret configured — realtime is simply off in that environment, not failing.
 * Raised as UnauthorizedError because that is the one refusal centrifuge does
 * not retry: anything else has every signed-in tab asking again forever.
 */
const REALTIME_DISABLED = "realtime is not configured on this server"

/**
 * Never cached: the token is short-lived and bound to the effective identity,
 * and centrifuge asks for it only when it needs a fresh one. A session denial
 * (no session, impersonation revoked) and a schema the server does not have are
 * refusals — neither changes by asking again. Everything else — a resolver
 * error, a timeout, a network failure — is left retryable: centrifuge backs off
 * and asks again.
 */
export async function fetchRealtimeToken(client: ApolloClient): Promise<string> {
  try {
    const { data } = await client.query({
      query: RealtimeConnectionTokenDocument,
      fetchPolicy: "no-cache",
      // The token is bound to the identity whose credentials fetched it. A
      // session restarted by an identity change would otherwise be handed the
      // answer to the request the previous one left in flight — stopping a
      // session does not cancel its query — and open the old user's channel.
      context: { queryDeduplication: false },
    })
    const credentials = data?.realtimeConnectionToken
    if (!credentials) throw new UnauthorizedError(REALTIME_DISABLED)
    if (!credentials.token) throw new Error("The realtime token is missing from the answer")
    return credentials.token
  } catch (error) {
    if (CombinedGraphQLErrors.is(error) && error.errors.some(isPermanent)) {
      throw new UnauthorizedError(error.message)
    }
    throw error
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null

/**
 * The `sub` claim of a JWT — the user the server issued the token for. No
 * signature check: the client only needs to know whose channel to join, and
 * the server verifies the signature when the connection opens.
 */
export function jwtSubject(token: string): string | null {
  const payload = token.split(".")[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const claims: unknown = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")))
    return isRecord(claims) && typeof claims.sub === "string" ? claims.sub : null
  } catch {
    return null
  }
}
